import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {GeoStatusState, GeoStatusTone, GEO_STATE_TONE} from '../geoStatusState';

import styles from './ProgressCircle.module.scss';

const RING_BY_TONE: Record<GeoStatusTone, string> = {
    success: styles.ringSuccess,
    warning: styles.ringWarning,
    problem: styles.ringProblem,
    neutral: styles.ringNeutral,
};

const TRACK_BY_TONE: Record<GeoStatusTone, string> = {
    success: styles.trackSuccess,
    warning: styles.trackWarning,
    problem: styles.trackProblem,
    neutral: styles.trackNeutral,
};

interface IProgressCircleProps {
    success: number;
    total: number;
    state: GeoStatusState;
}

const ProgressCircle: React.FC<IProgressCircleProps> = ({success, total, state}) => {
    const isInactive = state === 'paused' || state === 'waiting';
    const ok = isInactive ? 0 : Math.max(success, 0);
    const safeTotal = total > 0 ? total : 1;
    const ratio = Math.min(Math.max(ok / safeTotal, 0), 1);
    const radius = 32;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - ratio);
    const tone = GEO_STATE_TONE[state];

    return (
        <div className={styles.circleWrapper}>
            <svg width="80" height="80" viewBox="0 0 80 80" className={styles.circleSvg}>
                <circle
                    cx="40"
                    cy="40"
                    r={radius}
                    fill="none"
                    strokeWidth="6"
                    className={cn(styles.ringTrack, TRACK_BY_TONE[tone])}
                />
                <circle
                    cx="40"
                    cy="40"
                    r={radius}
                    fill="none"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    className={cn(styles.ringValue, RING_BY_TONE[tone])}
                    transform="rotate(-90 40 40)"
                />
            </svg>
            <div className={styles.circleText}>
                <span className={styles.circleNumber}>{ok}</span>
                <span className={styles.circleSubtitle}>{t`из ${total}`}</span>
            </div>
        </div>
    );
};

export default ProgressCircle;
